'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Play, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUserStore } from '@/utils/util_states';
import { ChatServiceError, resolveOrStartChat } from '@/services/puzzle-chat';
import { getLanguageDataByCode } from '@/i18n/language-data';
import { Ev, track, trackGuestLoginTrigger } from '@/utils/util_analytics';

type PublicPuzzleActionsProps = {
    puzzleId: string;
    title: string;
    languageCode: string;
};

export default function PublicPuzzleActions({ puzzleId, title, languageCode }: PublicPuzzleActionsProps) {
    const router = useRouter();
    const langData = getLanguageDataByCode(languageCode);
    const { isLogin, authReady } = useUserStore();
    const [isStarting, setIsStarting] = useState(false);
    const [copied, setCopied] = useState(false);

    async function startPuzzle() {
        if (!authReady || isStarting) return;
        track(Ev.discover_click, { puzzle_id: puzzleId, source: 'public_puzzle' });
        if (!isLogin) {
            trackGuestLoginTrigger('public_puzzle', { puzzle_id: puzzleId });
            toast.info(langData.profile_login_now);
            router.push('/login');
            return;
        }

        setIsStarting(true);
        try {
            const { chatId } = await resolveOrStartChat(puzzleId);
            router.push(`/chat/${chatId}`);
        } catch (error) {
            if (error instanceof ChatServiceError && error.code === 'need_login') {
                toast.info(langData.profile_login_now);
                router.push('/login');
            } else {
                console.error('Unable to start public puzzle:', error);
                toast.error(langData.chat_load_failed);
            }
        } finally {
            setIsStarting(false);
        }
    }

    async function sharePuzzle() {
        const url = `${window.location.origin}${window.location.pathname}`;
        try {
            if (navigator.share) {
                await navigator.share({ title, url });
                return;
            }
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = url;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                const didCopy = document.execCommand('copy');
                textarea.remove();
                if (!didCopy) throw new Error('Clipboard is unavailable');
            }
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
            toast.success(`${langData.app_share} ✓`);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') return;
            console.error('Unable to share puzzle:', error);
            toast.error(langData.chat_send_failed);
        }
    }

    return (
        <div className="flex flex-wrap gap-3">
            <button
                type="button"
                onClick={startPuzzle}
                disabled={!authReady || isStarting}
                className="inline-flex min-w-36 items-center justify-center gap-2 bg-foreground px-6 py-3 text-sm font-semibold text-background transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-50"
            >
                <Play size={16} fill="currentColor" />
                {isStarting ? langData.chat_loading : langData.app_play}
            </button>
            <button
                type="button"
                onClick={sharePuzzle}
                aria-label={copied ? `${langData.app_share} ✓` : langData.app_share}
                data-testid="public-puzzle-share"
                className="inline-flex items-center justify-center gap-2 border border-border bg-card px-5 py-3 text-sm text-muted-foreground transition-colors hover:border-lamp/30 hover:text-foreground"
            >
                <Share2 size={16} />
                {copied ? `${langData.app_share} ✓` : langData.app_share}
            </button>
        </div>
    );
}
